import '@/assets/css/main.css'
import 'app-components/style.css'
import 'app-components/print.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ui from '@nuxt/ui/vue-plugin'
import { addCollection } from '@iconify/vue'
import { icons as lucideIcons } from '@iconify-json/lucide'
import { AppService, Money, diContainer, AppStoreType, ModelsStoreType, IErrorReporter, IHtmlGen, II18n, ISettings, LocaleType, brandTheme } from 'app-core'

import VueApexCharts from 'vue3-apexcharts'
import VueQrcode from '@chenfengyuan/vue-qrcode'

import App from '@/App.vue'
import i18n from '@/i18n'
import router from '@/router'
import { setCurrentLocale } from '@/composables/useLocale'
import { useAppStore } from '@/stores/useAppStore'
import { installTableRowKeyboard } from '@/utilities/tableRowKeyboard'
import { installOverlayLeaveFallback } from '@/utilities/overlayLeaveFallback'
import { useModelsStore, createHtmlGen, Receipt } from 'app-components'
import { BRAND_VAR_NAMES, deriveBrandVars } from 'app-theme'
import errorReporter from '@/wrappers/ErrorReporter'
import packageJson from '../package.json'

// Offline icon resolution — register collections at bootstrap so UIcon never hits
// the Iconify network API (tech spec G2).
addCollection(lucideIcons)

// Paddle billing — same condition and setup as old admin main.ts
if (window.Paddle && import.meta.env.VITE_ENABLE_BILLING === 'true' && import.meta.env.VITE_PADDLE_VENDOR_ID) {
    window.Paddle.Setup({ vendor: parseInt(import.meta.env.VITE_PADDLE_VENDOR_ID) })
}

/**
 * Verbatim port from old admin main.ts: extract and strip SSO query params.
 */
function processUrl(): { tntoken?: string, token?: string, locale?: LocaleType, url?: string } {
    const currentUrl = new URL(window.location.href)
    const tntoken = currentUrl.searchParams.get('tntoken') || undefined
    const token = currentUrl.searchParams.get('token') || undefined
    const locale = currentUrl.searchParams.get('locale') as LocaleType || undefined

    if (tntoken || token) {
        currentUrl.searchParams.delete('tntoken')
        currentUrl.searchParams.delete('token')
        currentUrl.searchParams.delete('locale')
        return { tntoken, token, locale, url: currentUrl.pathname + currentUrl.search }
    }

    return { tntoken, token, locale }
}

async function initializeApp() {
    const pinia = createPinia()

    // Create app WITHOUT router — it is installed only after AppService init completes,
    // which replaces the old router.continueExecution deferred-navigation mechanism.
    const app = createApp(App)
        .use(pinia)
        .use(ui)
        .use(i18n)

    // Global components — old admin registered both (main.ts:38-39). The <qrcode> tag
    // is consumed in 9 files incl. the legal print templates, so the old template tag
    // name is registered explicitly (the v2 package's own name differs).
    app.component('Apexchart', VueApexCharts)
    // Registered under the component's own name ('vue-qrcode') — the tag the
    // shared receipt bodies render in the on-screen preview (POS idiom) —
    // AND under the old-admin 'qrcode' alias still used by Orders' import QR
    // and the tableSettings panels (P7 walk caught the missing alias).
    app.component(String(VueQrcode.name), VueQrcode)
    app.component('qrcode', VueQrcode)

    errorReporter.setUpErrorReporting(app)

    // Clickable list rows open on Enter / Space too (Nuxt UI rows react to clicks only).
    installTableRowKeyboard()

    // A menu or dialog closed while the browser prints still leaves the screen.
    installOverlayLeaveFallback()

    const appStore = useAppStore() as AppStoreType
    const modelsStore = useModelsStore()

    const settings: ISettings = {
        // Matches the package name and old admin's API/reporting identity
        // (the folder swap renamed admin-next → admin, 2026-07-11).
        appName: 'admin',
        version: packageJson.version,
        certifiedVersion: packageJson.certifiedVersion,
        env: JSON.parse(JSON.stringify(import.meta.env).replace(/VITE_/g, '')),
        // ISettings['electron'] (app-core) and the preload-typed window.electron
        // (types/global.d.ts) are structurally disjoint declarations of the same runtime
        // object; aligning them means editing app-core types (out of initiative scope).
        // Same cast posture as old admin's JS.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        electron: window.electron as any,
        onUnauthorized: (isTenantUnauthorized: boolean) => {
            if (isTenantUnauthorized) router.push({ name: 'tenantLogin' }).catch(() => {})
            else router.push({ name: 'userLogin' }).catch(() => {})
        },
        onLocaleChange: (locale: string) => {
            // Old admin: swap Vuetify lang pack + Vue.prototype.locale + window.locale.
            // window.locale kept for parity; in-app readers use the reactive useLocale()
            // (audit F7 — OrderList + print templates read the bare `locale` global).
            window.locale = locale
            setCurrentLocale(locale)
            document.documentElement.setAttribute('lang', locale)
        }
    }

    diContainer.register<AppStoreType>('store', appStore)
    diContainer.register<ModelsStoreType>('modelsStore', modelsStore)
    diContainer.register<II18n>('i18n', i18n.global)
    diContainer.register<ISettings>('settings', settings)
    diContainer.register<IErrorReporter>('errorReporter', errorReporter)
    // Receipts render through the shared app-components family (AD-8/O5) — the
    // same registrations POS and sap carry.
    diContainer.register<IHtmlGen>('htmlGen', createHtmlGen({ i18n }))
    diContainer.register('orderRenderComponent', Receipt)

    // Brand theme adapter — the brand lands as the --brand-* inline var
    // family on <html> (deriveBrandVars); theme.css/main.css consume them as
    // var(--brand-*, <stock>) fallbacks, so the theme blocks keep owning
    // light vs dark and clearing the vars restores the stock look exactly.
    brandTheme.registerAdapter(brand => {
        const root = document.documentElement
        const vars = brand ? deriveBrandVars(brand.colors) : null
        for (const name of BRAND_VAR_NAMES) {
            if (vars) root.style.setProperty(name, vars[name])
            else root.style.removeProperty(name)
        }
    })

    AppService.setUpDevelopmentMode()

    // Initialize the app — same call + args as old admin
    const { tntoken, token, locale, url } = processUrl()
    await AppService.initializeAdminApp({ tntoken, token, locale })

    brandTheme.applyFromLocale()

    // Router installed only now — guards run against fully initialized state.
    app.use(router)

    return { app, url }
}

async function startApp() {
    const { app, url } = await initializeApp()

    if (url) router.replace(url)

    app.mount('#app')
}

startApp()

// Money kept importable app-wide (no prototype globals in Vue 3 — views import explicitly).
export { Money }
